/* =============================================================================
 * CCR analytics — GA4 / GTM dataLayer event layer, consent-aware.
 * All vendor IDs come from CCR.config (theme settings), never hardcoded.
 * Public API:  CCR.track(eventName, payload)
 * Standard events: page_view, scroll_depth, cta_click, form_start,
 *   form_submit, add_to_cart, checkout_click, purchase.
 * CCR.track no-ops without analytics consent. Vendor pixels (GA4/GTM/Meta/
 * Ads/AdRoll) load only after the matching consent category is granted.
 * Revoking after a grant reloads the page so in-memory pixels drop.
 * ========================================================================== */
(function () {
  'use strict';
  var CCR = window.CCR || (window.CCR = {});
  var cfg = CCR.config || {};
  window.dataLayer = window.dataLayer || [];

  var STANDARD = ['page_view', 'scroll_depth', 'cta_click', 'form_start',
    'form_submit', 'add_to_cart', 'checkout_click', 'purchase',
    'view_item', 'begin_checkout', 'experiment_exposure'];

  function baseContext() {
    var exp = (CCR.experiments && CCR.experiments.activeAssignments)
      ? CCR.experiments.activeAssignments() : {};
    return {
      client_id: CCR.clientId,
      page_path: window.location.pathname,
      page_title: document.title,
      utm: CCR.utm || {},
      experiments: exp,
      consent: CCR.consent ? CCR.consent.all() : {}
    };
  }

  /* ----- direct GA4 forwarding (gtag) ----------------------------------- *
   * gtag.js ignores GTM-style dataLayer objects, so when a Measurement ID is
   * set but NO GTM container is configured, custom events (experiment_exposure,
   * exp_conversion, cta_click, …) would never reach GA4. This bridges them via
   * gtag('event', …). When a GTM container IS configured, GTM owns routing and
   * we don't send (no double-counting). gtag only exists after analytics
   * consent loads it, so pre-consent events are correctly not forwarded.       */
  function ga4Forward(name, source) {
    if (!cfg.ga4_measurement_id || cfg.gtm_container_id) return;
    if (typeof window.gtag !== 'function') return;
    var params = {};
    Object.keys(source || {}).forEach(function (k) {
      if (k === 'event' || k === 'ts') return;
      var v = source[k], t = typeof v;
      if (v != null && (t === 'string' || t === 'number' || t === 'boolean')) params[k] = v;
    });
    try { window.gtag('event', name, params); } catch (e) {}
  }
  CCR.ga4Forward = ga4Forward;

  /* ----- public: push an event into the dataLayer ----------------------- */
  CCR.track = function (event, payload) {
    if (!event) return;
    // Do not queue behavioral events before analytics consent (dataLayer can
    // be drained by GTM after a later grant).
    if (!CCR.consent || !CCR.consent.has('analytics')) return;
    var record = Object.assign({ event: event, ts: Date.now() },
      baseContext(), payload || {});
    window.dataLayer.push(record);
    ga4Forward(event, record);

    // Per-experiment companion event with FLAT params (GA4 custom dimensions).
    // A page can run several experiments at once, so a single event can't carry
    // one experiment_id/variant_id accurately — we emit one `exp_conversion`
    // per active experiment whose PRIMARY METRIC equals this event (so a test
    // measured by cta_click is attributed on cta_click, add_to_cart on add_to_cart,
    // form_submit on form_submit, etc.). Read by the GA4 readers.
    if (CCR.experiments && CCR.experiments.activeAssignments) {
      var assignments = CCR.experiments.activeAssignments();
      var byId = {};
      (CCR.experiments.registry || []).forEach(function (e) { byId[e.id] = e; });
      Object.keys(assignments).forEach(function (expId) {
        var exp = byId[expId];
        if (!exp || exp.primary_metric !== event) return;
        var companion = {
          event: 'exp_conversion',
          experiment_id: expId,
          variant_id: assignments[expId],
          exp_conversion_event: event,
          client_id: CCR.clientId,
          consent: CCR.consent ? CCR.consent.all() : {},
          ts: Date.now()
        };
        if (payload && payload.value != null) companion.value = payload.value;
        window.dataLayer.push(companion);
        ga4Forward('exp_conversion', companion);
      });
    }

    // mirror to experiment engine for metric attribution
    if (CCR.experiments && CCR.experiments.recordEvent) {
      CCR.experiments.recordEvent(event, record);
    }
    if (cfg.debug) {
      try { console.debug('[CCR:track]', event, record); } catch (e) {}
    }
    return record;
  };
  CCR.STANDARD_EVENTS = STANDARD;

  /* ----- vendor pixel loaders (consent-gated, idempotent) --------------- */
  var loaded = {};
  var loadedAnalytics = false;
  var loadedAds = false;
  function injectScript(src, attrs) {
    var s = document.createElement('script');
    s.async = true; s.src = src;
    Object.keys(attrs || {}).forEach(function (k) { s.setAttribute(k, attrs[k]); });
    document.head.appendChild(s);
    return s;
  }

  function loadGtag(id) {
    if (loaded[id]) return;
    loaded[id] = true;
    injectScript('https://www.googletagmanager.com/gtag/js?id=' + id);
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
  }

  function loadAnalytics() {
    // GTM container (preferred router) — loads once analytics consent given.
    if (cfg.gtm_container_id && !loaded[cfg.gtm_container_id]) {
      loaded[cfg.gtm_container_id] = true;
      injectScript('https://www.googletagmanager.com/gtm.js?id=' + cfg.gtm_container_id);
    }
    if (cfg.ga4_measurement_id) {
      loadGtag(cfg.ga4_measurement_id);
      window.gtag('config', cfg.ga4_measurement_id, { send_page_view: false });
    }
    loadedAnalytics = true;
  }

  function loadAds() {
    if (cfg.google_ads_conversion_id) {
      loadGtag(cfg.google_ads_conversion_id);
      window.gtag('config', cfg.google_ads_conversion_id);
    }
    if (cfg.meta_pixel_id && !loaded['fb']) {
      loaded['fb'] = true;
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
      (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq('init', cfg.meta_pixel_id);
      window.fbq('track', 'PageView');
    }
    if (cfg.adroll_adv_id && cfg.adroll_pix_id && !loaded['adroll']) {
      loaded['adroll'] = true;
      window.adroll_adv_id = cfg.adroll_adv_id;
      window.adroll_pix_id = cfg.adroll_pix_id;
      injectScript('https://s.adroll.com/j/' + cfg.adroll_adv_id + '/roundtrip.js');
    }
    loadedAds = true;
  }

  function syncConsent(state) {
    if (state.analytics) loadAnalytics();
    if (state.ads) loadAds();
    // Drop in-memory vendor pixels after revoke (one reload; next load stays denied).
    if ((loadedAnalytics && !state.analytics) || (loadedAds && !state.ads)) {
      try { window.location.reload(); } catch (e) {}
    }
  }

  CCR.ready(function () {
    if (CCR.consent.has('analytics')) loadAnalytics();
    if (CCR.consent.has('ads')) loadAds();
    CCR.consent.onChange(syncConsent);

    // page_view only reaches dataLayer / vendors when analytics consent is on.
    CCR.track('page_view', window.CCR_PAGE_CONTEXT || {});

    /* ----- automatic scroll-depth tracking (25/50/75/100) ------------- */
    var marks = [25, 50, 75, 100], fired = {};
    var onScroll = function () {
      var h = document.documentElement;
      var pct = Math.round((h.scrollTop + window.innerHeight) /
        h.scrollHeight * 100);
      marks.forEach(function (m) {
        if (pct >= m && !fired[m]) {
          fired[m] = true;
          CCR.track('scroll_depth', { depth: m });
        }
      });
    };
    window.addEventListener('scroll', throttle(onScroll, 400), { passive: true });

    /* ----- delegated CTA + checkout click tracking -------------------- */
    document.addEventListener('click', function (e) {
      var el = e.target.closest('[data-ccr-cta]');
      if (el) {
        CCR.track('cta_click', {
          cta_id: el.getAttribute('data-ccr-cta'),
          cta_text: (el.textContent || '').trim().slice(0, 80),
          cta_location: el.getAttribute('data-ccr-location') || ''
        });
      }
      var co = e.target.closest('[name="checkout"],[data-ccr-checkout]');
      if (co) CCR.track('checkout_click', {});
    });
  });

  function throttle(fn, wait) {
    var t = 0;
    return function () {
      var now = Date.now();
      if (now - t >= wait) { t = now; fn(); }
    };
  }
})();
